<template>
  <v-container fluid v-if="task" class="pt-0 pr-0" v-resize="onResize">
    <back-link-component :to="{ name: 'TaskListAsList' }" :label="$tt('feed.trajectories')" />
    <v-layout :class="{ column: isMobile }">
      <v-sheet :width="mapWidth" class="pr-4">
        <v-row align="center" class="pt-0">
          <v-col cols="12" md="8" class="py-0">
            <!-- 作業日付 -->
            <v-layout align-center justify-start>
              <v-col cols="auto" class="pb-0 py-6">
                <v-sheet class="transparent">
                  <h1>
                    {{ task.createdDateTime | aidToYearMonthDay }}
                    <span class="subtitle-1 font-weight-bold">
                      {{ task.startTime | aidToHourMin }}-{{ task.endTime | aidToHourMin }}
                    </span>
                  </h1>
                </v-sheet>
              </v-col>
            </v-layout>

            <!-- 圃場 -->
            <v-sheet class="transparent pl-2" v-if="field">
              <v-row>
                <v-col cols="12" class="pb-0">
                  <v-icon>place</v-icon>
                  {{ field.name }}
                  <v-icon left class="pl-2" @click="showFieldDetail(field)">mdi-pencil</v-icon>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="pl-4">
                  <div v-for="(planting, i) in plantings" :key="i">
                    <planting-indicator-component :planting="planting" small :display-year="false" />
                  </div>
                </v-col>
              </v-row>
            </v-sheet>
          </v-col>
          <!-- Account -->
          <v-col cols="12" md="4" class="pt-0">
            <v-layout align-center justify-end class="transparent px-2 pt-4">
              <v-layout class="font-weight-bold mb-0" align-center justify-end>
                {{ myAccount.nickname }}
                <user-icon-component
                  class="px-2 pb-1"
                  size="60"
                  :showNickname="true"
                  :iconUrl="myAccount.iconUrl"
                  :nickname="myAccount.nickname"
                />
              </v-layout>
            </v-layout>
          </v-col>
        </v-row>
        <div style="position:relative;">
          <v-row class="mt-0">
            <v-col cols="12" class="pt-0" style="position:relative;">
              <!-- ゲージセレクター -->
              <map-task-gauge-component @change="onGaugeModeChanged" :theme="echartsTheme" />
              <gmap-map
                ref="googleMapRef"
                :center="currentMapCenterForGoogle"
                :zoom="zoomLevel"
                :options="{
                  streetViewControl: false,
                  rotateControl: false,
                  gestureHandling: 'greedy',
                  mapTypeControlOptions: {
                    position: 2,
                  },
                  mapId: vectorMapId,
                }"
                map-type-id="roadmap"
                style="height: 50vh"
              >
                <GmapMarker :position="marker.position" :icon="marker.icon" :clickable="true" />
              </gmap-map>
            </v-col>
          </v-row>
          <v-row class="mt-0" v-if="!hasFreeFunctionality()">
            <v-col cols="12">
              <e-chart ref="composedChart" :options="composedChartOptions" autoresize :theme="echartsTheme" />
              <v-layout justify-end v-if="isMyTask">
                <v-btn small outlined color="primary" class="mt-1" @click="openRangeSave()">
                  <v-icon small left>mdi-content-cut</v-icon>
                  {{ $tt('feed.saveRangeAsWork') }}
                </v-btn>
              </v-layout>
            </v-col>
          </v-row>
        </div>
      </v-sheet>
      <v-sheet :width="infoWidth" :class="{ 'pr-2': isMobile }">
        <v-layout align-center justify-end v-resize="infoHeightResize" id="infoArea">
          <div v-if="!isMobile" class="pa-0 pl-1 " @click="changeInfoVisible()" style="cursor:pointer;">
            <div class="px-0 py-12  aid-bg-darkgrey">
              <v-icon v-if="isInfo">mdi-play</v-icon>
              <v-icon class="mdi-rotate-180" v-else>mdi-play</v-icon>
            </div>
          </div>
          <v-sheet class="px-0" v-if="isInfo" :width="infoInnerWidth">
            <v-sheet color="aid-bg-darkgrey" class="px-0 pb-8 pt-0">
              <!-- 天気 OpenMeteo -->
              <v-row no-gutters class="px-4" v-if="isWeatherInfoAvailable">
                <v-row justify="center" align-center>
                  <v-col cols="auto" md="6">
                    <div class="d-flex flex-column justify-space-between align-center">
                      <v-img width="100" class=" align-center" :src="weatherInfoFromOpenMeteo.iconUrl" />
                    </div>
                    <h3 class="pb-0 mb-0 text-center">
                      {{ weatherInfoFromOpenMeteo.temperature | aidTemperature(2) }}
                    </h3>
                  </v-col>
                  <v-col cols="auto" md="6" class="pt-8">
                    <v-row justify="center">
                      <v-col cols="3" class="pr-0 py-0">
                        <v-layout align-center justify-end>
                          <v-icon width="32" color="blue">
                            mdi-water
                          </v-icon>
                        </v-layout>
                      </v-col>
                      <v-col cols="9" class="pl-0 py-0">
                        <v-layout align-center justify-start>
                          <v-sheet class="ml-2 font-weight-bold  subtitle-2 transparent">
                            {{ weatherInfoFromOpenMeteo.relativeHumidity | aidPercent }}
                          </v-sheet>
                        </v-layout>
                      </v-col>
                    </v-row>
                    <v-row align-center>
                      <v-col cols="3" class="pr-0 py-0">
                        <v-layout align-center justify-end>
                          <v-icon width="32" color="blue">
                            mdi-weather-windy
                          </v-icon>
                        </v-layout>
                      </v-col>
                      <v-col cols="9" class="pl-0 py-0">
                        <v-layout align-center justify-start>
                          <v-sheet class="ml-2 font-weight-bold  subtitle-2 transparent">
                            {{ toMeterPerSecond(weatherInfoFromOpenMeteo.windSpeed) | aidWindSpeedText }}
                          </v-sheet>
                        </v-layout>
                      </v-col>
                    </v-row>
                    <v-row align-center>
                      <v-col cols="3" class="pr-0 py-0">
                        <v-layout align-center justify-end>
                          <v-icon width="32" color="blue">
                            mdi-weather-rainy
                          </v-icon>
                        </v-layout>
                      </v-col>
                      <v-col cols="9" class="pl-0 py-0">
                        <v-layout align-center justify-start>
                          <v-sheet class="ml-2 font-weight-bold  subtitle-2 transparent">
                            {{ weatherInfoFromOpenMeteo.precipitation | aidMilliMetres }}
                          </v-sheet>
                        </v-layout>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-row>
              <v-row v-else class="py-11"></v-row>
              <v-row no-gutters class="px-4">
                <v-col cols="12">
                  <v-divider />

                  <!-- 作業推測（Grow） -->
                  <v-row v-if="isGrowAvailable">
                    <v-col cols="12">
                      <v-sheet class="ml-2 font-weight-bold subtitle-2 transparent">
                        作業推測
                      </v-sheet>
                    </v-col>
                    <v-col cols="12">
                      <v-simple-table dense>
                        <template v-slot:default>
                          <tbody>
                            <tr>
                              <th class="text-left">有効速度</th>
                              <td>{{ effectiveSpeed }} km/h</td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                      <v-simple-table dense>
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">作物</th>
                              <th class="text-left">作業機</th>
                              <th class="text-left">作業</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(p, i) in predictions" :key="i">
                              <td>{{ p.crop_name_jp }}</td>
                              <td>{{ p.machine_name_jp }}</td>
                              <td>{{ p.operation_name_jp }}</td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-col>
                  </v-row>
                  <v-divider v-if="isGrowAvailable" />

                  <!-- 作業 -->
                  <v-row>
                    <v-col cols="12" class="py-2">
                      <v-layout align-center justify-start>
                        <v-sheet width="24" class="transparent">
                          <v-img class="align-center" src="/icons/WorkHistory_icon_work.png" />
                        </v-sheet>
                        <v-sheet class="ml-2 font-weight-bold  subtitle-2 transparent">
                          {{ $tt('customOperationType') }}
                        </v-sheet>
                        <v-icon left class="pl-2" @click="openTaskMetaDataEditor()">mode_edit_outline</v-icon>
                        <v-btn small color="primary" class="ml-2" @click="isShareDialogVisible = true">
                          <v-icon small left>mdi-share-variant</v-icon>
                          {{ $tt('feed.share') }}
                        </v-btn>
                      </v-layout>
                    </v-col>
                    <v-col cols="12" class="py-2 pl-10 pt-0">
                      <v-sheet class="ml-2  text-left subtitle-2 transparent" style="overflow-wrap:  break-word;">
                        {{ getLocalizedOperationType(task.operationType) }}<br />
                        {{ task.name }}
                      </v-sheet>
                    </v-col>
                  </v-row>
                  <v-divider />

                  <!-- 圃場面積 -->
                  <v-row>
                    <v-col cols="12" class="py-2">
                      <v-layout align-center justify-start>
                        <v-sheet width="24" class="transparent">
                          <v-img class="align-center" src="/icons/WorkHistory_icon_area.png" />
                        </v-sheet>
                        <v-sheet class="ml-2 font-weight-bold  subtitle-2 transparent">{{ $tt('customArea') }}</v-sheet>
                      </v-layout>
                    </v-col>
                    <v-col cols="12" class="py-2 pl-10 pt-0" v-if="field">
                      <v-sheet class="ml-2 text-left subtitle-2 transparent">
                        {{ field.area | aidAreaText }}
                      </v-sheet>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <!-- 作業面積 -->
                  <v-row>
                    <v-col cols="12" class="py-2">
                      <v-layout align-center justify-start>
                        <v-sheet width="24" class="transparent">
                          <v-img class="align-center" src="/icons/WorkHistory_icon_workingarea.png" />
                        </v-sheet>
                        <v-sheet class="ml-2 font-weight-bold  subtitle-2 transparent">
                          {{ $tt('customWorkedArea') }}
                        </v-sheet>
                      </v-layout>
                    </v-col>
                    <v-col cols="12" class="py-2 pl-10 pt-0">
                      <v-sheet class="ml-2 text-left subtitle-2 transparent">
                        {{ task | aidTaskRecordArea | aidAreaText }}
                      </v-sheet>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <!-- 作業重なり面積 -->
                  <v-row v-if="isOverlappedAreaDisplayUser">
                    <v-col cols="12" class="py-2">
                      <v-layout align-center justify-start>
                        <v-sheet width="24" class="transparent">
                          <v-img class="align-center" src="/icons/WorkHistory_icon_workingarea.png" />
                        </v-sheet>
                        <v-sheet class="ml-2 font-weight-bold  subtitle-2 transparent">
                          {{ $tt('customOverlappedArea') }}
                        </v-sheet>
                      </v-layout>
                    </v-col>
                    <v-col cols="12" class="py-2 pl-10 pt-0">
                      <v-sheet class="ml-2 text-left subtitle-2 transparent">
                        {{ task.overlappedArea | aidAreaText }}
                      </v-sheet>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <!-- 作業幅 -->
                  <v-row>
                    <v-col cols="12" class="py-2">
                      <v-layout align-center justify-start>
                        <v-sheet width="24" class="transparent">
                          <v-img class="align-center" src="/icons/WorkHistory_icon_workingwidth.png" />
                        </v-sheet>
                        <v-sheet class="ml-2 font-weight-bold  subtitle-2 transparent">
                          {{ $tt('customWorkWidth') }}
                        </v-sheet>
                      </v-layout>
                    </v-col>
                    <v-col cols="12" class="py-2 pl-10 pt-0">
                      <v-sheet class="ml-2 text-left subtitle-2 transparent">
                        {{ getWorkWidth(task) }}
                      </v-sheet>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <!-- 作業時間 -->
                  <v-row>
                    <v-col cols="12" class="py-2">
                      <v-layout align-center justify-start>
                        <v-sheet width="24" class="transparent">
                          <v-img class="align-center" src="/icons/WorkHistory_icon_workinghours.png" />
                        </v-sheet>
                        <v-sheet class="ml-2 font-weight-bold  subtitle-2 transparent">
                          {{ $tt('customWorkedTime') }}
                        </v-sheet>
                      </v-layout>
                    </v-col>
                    <v-col cols="12" class="py-2 pl-10 pt-0">
                      <v-sheet class="ml-2 text-left subtitle-2 transparent">
                        {{ task | aidTaskRecordWorkingTimeDurationFormatted }}
                      </v-sheet>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <!-- 走行距離 -->
                  <v-row>
                    <v-col cols="12" class="py-2">
                      <v-layout align-center justify-start>
                        <v-sheet width="24" class="transparent">
                          <v-img class="align-center" src="/icons/WorkHistory_icon_mileage.png" />
                        </v-sheet>
                        <v-sheet class="ml-2 font-weight-bold  subtitle-2 transparent"
                          >{{ $tt('customDistance') }}
                        </v-sheet>
                      </v-layout>
                    </v-col>
                    <v-col cols="12" class="py-2 pl-10 pt-0">
                      <v-sheet class="ml-2 text-left subtitle-2 transparent">
                        {{ task | aidTaskRecordDistance | aidDistanceText }}
                      </v-sheet>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <!-- 平均スピード -->
                  <v-row>
                    <v-col cols="12" class="py-2">
                      <v-layout align-center justify-start>
                        <v-sheet width="24" class="transparent">
                          <v-img class="align-center" src="/icons/WorkHistory_icon_runningspeed.png" />
                        </v-sheet>
                        <v-sheet class="ml-2 font-weight-bold  subtitle-2 transparent">
                          {{ $tt('customAverageSpeed') }}
                        </v-sheet>
                      </v-layout>
                    </v-col>
                    <v-col cols="12" class="py-2 pl-10 pt-0">
                      <v-sheet class="ml-2 text-left subtitle-2 transparent">
                        {{ task | aidTaskRecordAverageSpeed | aidSpeedText }}
                      </v-sheet>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <!-- GNSS取得元 -->
                  <v-row v-if="task.gnssSource">
                    <v-col cols="12" class="py-2">
                      <v-layout align-center justify-start>
                        <v-sheet width="24" class="transparent">
                          <v-img class="align-center" src="/icons/WorkHistory_icon_GNSS.png" />
                        </v-sheet>
                        <v-sheet class="ml-2 font-weight-bold  subtitle-2 transparent">
                          {{ $tt('navi.GeneralMainSettingMenuGnssSource') }}
                        </v-sheet>
                      </v-layout>
                    </v-col>
                    <v-col cols="12" class="py-2 pl-10 pt-0">
                      <v-sheet class="ml-2 text-left subtitle-2 transparent">
                        {{ task | aidGnssSourceText }}
                      </v-sheet>
                    </v-col>
                  </v-row>
                  <v-divider v-if="task.gnssSource" />
                  <!-- 備考 -->
                  <v-row>
                    <v-col cols="12" class="py-2">
                      <v-layout align-center justify-start>
                        <v-sheet width="24" class="transparent">
                          <v-img class="align-center" src="/icons/WorkHistory_icon_note.png" />
                        </v-sheet>
                        <v-sheet class="ml-2 font-weight-bold  subtitle-2 transparent">
                          {{ $tt('DocFormTaskRecordDetailDescriptionHint') }}
                        </v-sheet>
                        <v-icon left class="pl-2" @click="openTaskMetaDataEditor()">mode_edit_outline</v-icon>
                      </v-layout>
                    </v-col>
                    <v-col cols="12" class="py-2 pl-10 pt-0">
                      <v-sheet
                        class="ml-2  text-left subtitle-2 transparent font-weight-regular"
                        style="overflow-wrap:  break-word;"
                      >
                        {{ task.caption }}
                      </v-sheet>
                    </v-col>
                  </v-row>
                  <v-divider />
                </v-col>
              </v-row>
            </v-sheet>
          </v-sheet>
          <v-sheet class="px-0 aid-bg-darkgrey" width="20px" v-else :height="infoMockHeight"></v-sheet>
        </v-layout>
      </v-sheet>
    </v-layout>

    <!-- 作業name/caption編集 -->
    <v-dialog v-model="isNowShowingTaskMetaDataEditor" max-width="550px" persistent>
      <v-card>
        <v-card-title></v-card-title>
        <v-card-text>
          <v-select
            v-model="editingOperationType"
            :label="$tt('customOperationType')"
            :items="getAvailableOperationTypesOptions()"
          />
          <v-text-field :label="$tt('DocFormTaskRecordDetailFieldNameHint')" v-model="editingTaskName" />
          <v-textarea :label="$tt('DocFormTaskRecordDetailDescriptionHint')" v-model="editingTaskCaption" rows="3" />
        </v-card-text>
        <v-card-actions class="d-flex">
          <v-spacer />
          <v-btn right color="secondary" @click="closeTaskMetaDataEditor()">{{ $tt('customCancel') }}</v-btn>
          <v-btn right color="primary" @click="updateTaskMetaData()">{{ $tt('customSaveAction') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- 区間の派生記録保存 (F5-3: グラフの dataZoom で選んだ範囲を割合で保持) -->
    <v-dialog v-model="rangeSave.show" max-width="360">
      <v-card>
        <v-card-title class="subtitle-1">{{ $tt('feed.saveRangeAsWork') }}</v-card-title>
        <v-card-text>
          <div class="caption grey--text mb-2">{{ Math.round(rangeSave.from) }}% - {{ Math.round(rangeSave.to) }}%</div>
          <v-text-field v-model="rangeSave.name" dense :label="$tt('feed.workName')" />
          <v-textarea v-model="rangeSave.memo" dense rows="2" :label="$tt('feed.recordMemo')" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="rangeSave.show = false">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="primary" :loading="rangeSaving" :disabled="!rangeSave.name" @click="saveRangeAsWork()">
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <share-post-dialog v-model="isShareDialogVisible" :task-meta="shareTaskMeta" />
    <now-loading-overlay-component :nowLoading="nowLoading" />
  </v-container>
</template>

<script>
import MapTaskGaugeComponent from '@/components/map/MapTaskGaugeComponent'
import ChartMixin from '@/components/mixins/chart-mixin'
import MapMixin from '@/components/mixins/map-mixin'
import GoogleMapMixin from '@/components/mixins/google-map-mixin'
import OperationTypeMixin from '@/components/mixins/operation-type-mixin'
import WeatherInfoMixin from '@/components/mixins/weather-info-mixin'
import PlantingIndicatorComponent from '@/components/planting/PlantingIndicatorComponent'
import UserIconComponent from '@/components/user/UserIconComponent'
import NowLoadingOverlayComponent from '@/components/NowLoadingOverlayComponent'
import SharePostDialog from '@/components/feed/SharePostDialog'
import feedApiClient from '@/lib/feed-api-client'
import Toast from '@/lib/toast'
import Account from '@/lib/account'
import * as Constants from '@/lib/constants'
import {
  aidTaskRecordWorkWidth,
  aidTaskRecordArea,
  aidTaskRecordDistance,
  aidTaskRecordAverageSpeed,
  aidGnssSourceText,
} from '@/lib/filters'
import i18n from '@/lib/i18n'
import Operation from '@/lib/operation'
import Planting from '@/lib/planting'
import * as SystemOfUnits from '@/lib/system-of-units'
import UserInfo from '@/lib/user-info'
import UTCDateTime from '@/lib/utc-datetime'
import ECharts from 'vue-echarts'

require('@/lib/echarts/themes/dark')

const turf = require('@turf/turf')
const INFO_DEFAULT_WIDTH = '20%'
const MAP_DEFAULT_WIDTH = '80%'
const INFO_MIN_WIDTH = '5%'
const INFO_INNER_WIDTH = '90%'
const MAP_LARGE_WIDTH = '95%'
const MAX_WIDTH = '100%'

export default {
  name: 'TaskDetails',
  mixins: [MapMixin, ChartMixin, WeatherInfoMixin, OperationTypeMixin, GoogleMapMixin],

  components: {
    PlantingIndicatorComponent,
    MapTaskGaugeComponent,
    UserIconComponent,
    'e-chart': ECharts,
    NowLoadingOverlayComponent,
    SharePostDialog,
  },

  data() {
    return {
      isShareDialogVisible: false,
      isNowShowingTaskMetaDataEditor: false,
      editingOperationType: undefined,
      editingTaskName: undefined,
      editingTaskCaption: undefined,
      echartsTheme: null,
      aidGreen: Constants.CONNECT_GREEN,

      growOption: {
        includeCrop: true,
      },

      teskGeoJsons: { type: 'FeatureCollection', features: [] },
      selectedGaugeMode: Constants.MAP_TASK_COLORED_MODE_NONE,
      marker: {
        position: { lat: 0, lng: 0 },
        icon: Constants.TRACTOR_ICON_URLS[0],
      },
      isInfo: true,
      isMobile: false,
      infoWidth: INFO_DEFAULT_WIDTH,
      infoInnerWidth: INFO_INNER_WIDTH,
      mapWidth: MAP_DEFAULT_WIDTH,
      infoMockHeight: '0px',

      nowLoading: false,
      rangeSave: { show: false, from: 0, to: 100, name: '', memo: '' },
      rangeSaving: false,
    }
  },

  mounted: function() {
    if (this.isDarkMode) {
      this.echartsTheme = 'dark'
    } else {
      this.echartsTheme = null
    }
    // FIXME GROW GROWはEAPユーザー、もしくはisImpersonating=trueの場合にのみ有効
    if (this.isGrowAvailable) {
      this.predictOperation()
    }
    this.drawAllFieldsOnGoogleMap()
    this.$refs.googleMapRef.$mapPromise.then(map => {
      map.setTilt(0)
      map.data.addGeoJson(this.$store.getters.fieldGeoJsons)
    })

    this.setPolygonStyle()
    const points = this.$store.getters.currentTaskAllPointsFeature.geometry.coordinates
    const startPoint = points[0]
    this.marker.position.lat = startPoint[1]
    this.marker.position.lng = startPoint[0]

    if (this.eventBus) {
      let vm = this
      this.eventBus.$on(Constants.EVENT_SET_MAP_MARKER, function(index) {
        const point = points[index]
        vm.marker.position.lat = point[1]
        vm.marker.position.lng = point[0]
      })
    }
  },

  methods: {
    openRangeSave() {
      // dataZoom スライダーの現在範囲 (%) を読み取る
      let from = 0
      let to = 100
      try {
        const chart = this.$refs.composedChart && this.$refs.composedChart.chart
        const dz = chart && chart.getOption().dataZoom
        if (dz && dz[0]) {
          from = dz[0].start != null ? dz[0].start : 0
          to = dz[0].end != null ? dz[0].end : 100
        }
      } catch (e) {
        console.error(e)
      }
      this.rangeSave = { show: true, from, to, name: '', memo: '' }
    },
    async saveRangeAsWork() {
      const meta = this.shareTaskMeta
      if (!meta || !meta.session_id) return
      const frac = (this.rangeSave.to - this.rangeSave.from) / 100
      const source = { session_id: meta.session_id, task_group_id: meta.task_group_id }
      if (this.rangeSave.from > 0 || this.rangeSave.to < 100) {
        source.range = { from_pct: this.rangeSave.from, to_pct: this.rangeSave.to }
      }
      this.rangeSaving = true
      try {
        await feedApiClient.createWorkRecord({
          kind: 'merge',
          sources: [source],
          params: {},
          name: this.rangeSave.name,
          memo: this.rangeSave.memo,
          operation_type: this.task.operationType || null,
          started_at: this.task.startTime || null,
          cache: {
            distance_m: meta.distance_m != null ? Math.round(meta.distance_m * frac) : null,
            duration_min: meta.duration_min != null ? Math.round(meta.duration_min * frac) : null,
          },
        })
        Toast.i18nSuccess('feed.workSaved')
        this.rangeSave.show = false
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.loadError')
      } finally {
        this.rangeSaving = false
      }
    },
    onResize() {
      if (window.innerWidth < Constants.RESPONSIVE_BREAKPOINT_MD_SIZE) {
        this.isMobile = true
        this.openInfo()
        this.infoWidth = MAX_WIDTH
        this.mapWidth = MAX_WIDTH
        this.infoInnerWidth = MAX_WIDTH
      } else {
        if (this.isMobile) {
          this.infoWidth = INFO_DEFAULT_WIDTH
          this.mapWidth = MAP_DEFAULT_WIDTH
          this.infoInnerWidth = INFO_INNER_WIDTH
        }
        this.isMobile = false
      }
    },
    changeInfoVisible() {
      if (this.isInfo) {
        this.closeInfo()
      } else {
        this.openInfo()
      }
    },
    closeInfo() {
      this.infoHeightResize()
      this.isInfo = false
      this.infoWidth = INFO_MIN_WIDTH
      this.mapWidth = MAP_LARGE_WIDTH
    },
    openInfo() {
      this.isInfo = true
      this.infoWidth = INFO_DEFAULT_WIDTH
      this.mapWidth = MAP_DEFAULT_WIDTH
    },
    setPolygonStyle() {
      let geoJson = this.$store.getters.currentTaskPolygons.allPolygon
      switch (this.selectedGaugeMode) {
        case Constants.MAP_TASK_COLORED_MODE_NONE:
          geoJson = this.$store.getters.currentTaskPolygons.allPolygon
          break
        case Constants.MAP_TASK_COLORED_MODE_ALTITUDE:
          geoJson = this.$store.getters.currentTaskPolygons.altitudesPolygon
          break
        case Constants.MAP_TASK_COLORED_MODE_SPEED:
          geoJson = this.$store.getters.currentTaskPolygons.speedsPolygon
          break
        case Constants.MAP_TASK_COLORED_MODE_GNSS_NUM_OF_SATELLITES:
          geoJson = this.$store.getters.currentTaskPolygons.gnssNumOfSatellitesPolygon
          break
        case Constants.MAP_TASK_COLORED_MODE_GNSS_STATUS:
          geoJson = this.$store.getters.currentTaskPolygons.gnssStatusPolygon
          break
        case Constants.MAP_TASK_COLORED_MODE_GUIDANCE_ERROR:
          geoJson = this.$store.getters.currentTaskPolygons.guidanceErrorsPolygon
          break
        case Constants.MAP_TASK_COLORED_MODE_GUIDANCE_HEADING_ERROR:
          geoJson = this.$store.getters.currentTaskPolygons.guidanceHeadingErrorsPolygon
          break
        case Constants.MAP_TASK_COLORED_MODE_SLANT:
          geoJson = this.$store.getters.currentTaskPolygons.slantsPolygon
          break
        case Constants.MAP_TASK_COLORED_MODE_COLOR_CODED:
          geoJson = this.$store.getters.currentTaskPolygons.colorCodedFillPolygon
          break
      }

      this.$refs.googleMapRef.$mapPromise.then(map => {
        map.data.forEach(feature => {
          map.data.remove(feature)
        })
        map.data.addGeoJson(this.$store.getters.fieldGeoJsons)
        map.data.addGeoJson(geoJson)
        map.data.setStyle(feature => {
          const color = feature.getProperty('color')
          const layerIndex = feature.getProperty('layerIndex')
          if (color) {
            return {
              fillColor: color,
              fillOpacity: Constants.TRACK_FILL_OPACITY,
              strokeColor: color,
              strokeWeight: 1,
              zIndex: layerIndex + 100, //他のレイヤーも重なる可能性があるので一旦100
            }
          } else {
            return {
              fillColor: Constants.FIELD_FILL_COLOR,
              fillOpacity: Constants.FIELD_FILL_OPACITY,
              strokeColor: Constants.FIELD_FILL_COLOR,
              strokeWeight: 1,
              zIndex: 0,
            }
          }
        })
      })
    },
    toMeterPerSecond(speed) {
      return SystemOfUnits.toMeterPerSecFromKiloPerHour(speed)
    },
    getWorkWidth(task) {
      const workWidth = aidTaskRecordWorkWidth(task)
      return SystemOfUnits.getLengthTextFrom(workWidth, { format: SystemOfUnits.FORMAT_TRIPLE_ZERO })
    },

    showFieldDetail(field) {
      this.$store.dispatch('getAllFields')
      this.$store.dispatch('showFieldDetail2', field)
    },

    hasFreeFunctionality() {
      return UserInfo.hasFreeFunctionality()
    },

    onGaugeModeChanged(selectedGaugeMode) {
      const standardPlanNeededMessage = i18n.tt('StandardPlanIsNeededDialogMessage')
      this.selectedGaugeMode = selectedGaugeMode
      this.setPolygonStyle()
    },

    getAvailableOperationTypesOptions() {
      const language = i18n.getUserLang()
      return Operation.getAvailableOperationTypesForOptions(language)
    },

    openTaskMetaDataEditor() {
      this.isNowShowingTaskMetaDataEditor = true
      this.editingTaskName = this.task.name
      this.editingTaskCaption = this.task.caption
      this.editingOperationType = this.task.operationType
    },

    closeTaskMetaDataEditor() {
      this.isNowShowingTaskMetaDataEditor = false
      this.editingTaskName = undefined
      this.editingTaskCaption = undefined
      this.editingOperationType = undefined
    },

    updateTaskMetaData() {
      const updateRequest = {
        task: this.task,
        name: this.editingTaskName,
        caption: this.editingTaskCaption,
        metaDatas: { operationType: this.editingOperationType },
        onSuccess: this.updateTaskMetaDataSuccess,
        onFailure: this.updateTaskMetaDataFailure,
      }
      this.openNowLoading()
      this.$store.dispatch('updateTaskMetaData', updateRequest)
      this.closeTaskMetaDataEditor()
    },
    updateTaskMetaDataSuccess() {
      this.closeTaskMetaDataEditor()
      this.closeNowLoading()
    },
    updateTaskMetaDataFailure() {
      this.closeTaskMetaDataEditor()
      this.closeNowLoading()
    },
    openNowLoading() {
      this.nowLoading = true
    },
    closeNowLoading() {
      this.nowLoading = false
    },

    predictOperation() {
      // FIXME 作付が複数ある場合は、最初の一個だけ
      let masterCropId = -1
      if (this.plantings.length > 0) {
        masterCropId = this.plantings[0].masterCropId
      }
      let arg = { taskRecordItem: this.task, effectiveSpeed: this.$store.getters.currentTaskEffectiveSpeed }
      if (masterCropId >= 0) {
        arg.crop = masterCropId
      }
      this.$store.dispatch('predictOperation', arg)
    },
    infoHeightResize() {
      const infoAreaHeight = document.getElementById('infoArea').scrollHeight
      this.infoMockHeight = infoAreaHeight + 'px'
    },
  },

  computed: {
    field() {
      return this.$store.getters.currentField
    },
    weatherInfoFromOpenMeteo() {
      return this.$store.getters.currentTaskWeatherInfoFromOpenMeteo
    },
    task() {
      return this.$store.getters.currentTask
    },

    shareTaskMeta() {
      if (!this.task) return null
      let durationMin = null
      const start = Date.parse(this.task.startTime)
      const end = Date.parse(this.task.endTime)
      if (!isNaN(start) && !isNaN(end) && end > start) {
        durationMin = Math.round((end - start) / 60000)
      }
      // ルートパラメータ key = "{sessionId}-{taskGroupId}" (ID に '-' は含まれない)
      let sessionId = null
      let taskGroupId = null
      const key = this.$route.params.key
      if (key && key.indexOf('-') > 0) {
        sessionId = key.slice(0, key.indexOf('-'))
        taskGroupId = key.slice(key.indexOf('-') + 1)
      }
      const safeNum = v => (typeof v === 'number' && isFinite(v) ? v : null)
      let gnssText = null
      try {
        gnssText = this.task.gnssSource ? aidGnssSourceText(this.task) : null
      } catch (e) {
        gnssText = null
      }
      return {
        operation_type: this.task.operationType ? this.getLocalizedOperationType(this.task.operationType) : null,
        started_at: this.task.startTime || null,
        duration_min: durationMin,
        distance_m: safeNum(aidTaskRecordDistance(this.task)),
        area_m2: safeNum(aidTaskRecordArea(this.task)),
        width_m: safeNum(this.task.workWidthOnEnded),
        speed_ms: safeNum(aidTaskRecordAverageSpeed(this.task)),
        gnss: gnssText,
        session_id: sessionId,
        task_group_id: taskGroupId,
      }
    },

    myAccount() {
      return Account.getByTask(this.task)
    },

    isMyTask() {
      const taskUsername = this.task.username
      const me = this.$store.getters.loginUsername
      return me === taskUsername
    },

    isTaskMetaDataExists() {
      if (!this.task) {
        return false
      }
      if (this.task.name) {
        return true
      }
      if (this.task.caption) {
        return true
      }
      return false
    },

    isDarkMode() {
      if (this.$vuetify.theme.dark) {
        return true
      }
      return false
    },

    isWeatherInfoAvailable() {
      if (this.weatherInfoFromOpenMeteo) {
        return true
      }
      return false
    },

    isOverlappedAreaDisplayUser() {
      // https://github.com/agri-info-design/agribus-web2/issues/48
      return true
      // return UserGroupsInfo.match(UserGroupsInfo.OVERLAPED_AREA_DISPLAY_USER)
    },

    // FIXME GROW
    isGrowAvailable() {
      // FIXME 一旦利用停止
      // // GROWはEAPユーザー、もしくはisImpersonating=trueの場合にのみ有効
      // if (UserInfo.isImpersonating() || UserGroupsInfo.match(UserGroupsInfo.EAP_GROW)) {
      //   return true
      // }
      return false
    },

    // FIXME GROW
    predictions() {
      return this.$store.getters.growPredictions
    },

    // FIXME GROW
    effectiveSpeed() {
      return this.$store.getters.currentTaskEffectiveSpeed
    },

    plantings() {
      const field = this.field
      const startTime = UTCDateTime.toUtcMoment(this.task.startTime)
      return Planting.listForFieldAndDate(field, startTime)
    },
  },

  watch: {
    isDarkMode: function(newVal, oldVal) {
      if (newVal) {
        this.echartsTheme = 'dark'
      } else {
        this.echartsTheme = null
      }
    },
  },
}
</script>

<style scoped>
.echarts {
  width: 100%;
  height: 30vh;
}
</style>
